import React, {useState, useEffect, useRef} from 'react';
import {View, Text, Image, Pressable, Modal} from 'react-native';
import SignatureCanvas from 'react-signature-canvas';
import ReactSignatureCanvas from 'react-signature-canvas';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import DynamicAutocomplete from '../DynamicControls/DynamicAutocomplete';
import ConfirmModal from '../Shared/ConfirmModal';
import Icon from '../Shared/Icon';
import {
  DynamicAttachment,
  DynamicSignatureListProps,
} from '../../Types/ControlTypes';
import {ListDataTypes} from '../../Constants/ListDataTypes';
import {ControlsStyleSheet} from '../../Styles/Shared/Controls';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import {ReportsStyleSheet} from '../../Styles/ReportsStyles';
import Colors from '../../Styles/Shared/Colors';
import {MongoIdSqlId} from '../../Types/DtoTypes';
import {IAttachmentService} from '../../Services/Interfaces/IAttachmentService';
import AttachmentService from '../../Services/AttachmentService';
import {UriSasPair} from './DynamicAttachments';

export declare type SignatureListItem = {
  index: number;
  person: string;
  personName: string;
  attachment?: DynamicAttachment;
};

const DynamicSignatureList = (
  props: DynamicSignatureListProps,
): React.ReactElement => {
  const [value, setValue] = useState(props.value ?? '');
  const [items, setItems] = useState<SignatureListItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<SignatureListItem | null>(
    null,
  );
  const [isShowingModal, setIsShowingModal] = useState(false);
  const [expandedItems, setExpandedItems] = useState<number[]>([]);
  const [itemToRemove, setiIemToRemove] = useState<SignatureListItem | null>(
    null,
  );
  const [sasUris, setSasUris] = useState<UriSasPair[]>([]);
  const [error, setError] = useState('');

  const signatureRef = React.createRef<ReactSignatureCanvas>();
  const [signatureShowErrors, setSignatureShowErrors] = useState(false);
  const [nameError, setNameError] = useState('');
  const [signatureError, setSignatureError] = useState('');
  const {people} = usePowerSyncData();

  const attachmentService: IAttachmentService = new AttachmentService(() => {});

  const deleteModalCommand = useRef({
    openModal: () => {},
    setOpenModal(func: () => void) {
      this.openModal = func;
    },
  });

  useEffect(() => {
    try {
      if (value) {
        let newItems = JSON.parse(value) as SignatureListItem[];
        if (newItems) {
          setItems(newItems);

          const getSasUri = async () => {
            let newSasUris: UriSasPair[] = [];
            for (let i = 0; i < newItems.length; i++) {
              try {
                if (newItems[i].attachment?.blobUri) {
                  let fullUri = await attachmentService.getSasUri(
                    newItems[i].attachment!.blobUri!,
                  );

                  newSasUris.push({
                    uri: newItems[i].attachment!.blobUri!,
                    sasUri: fullUri,
                  });
                }
              } catch (err) {
                //failed getting sasuri
              }
            }

            setSasUris(newSasUris);
          };

          getSasUri();
        }
      }
    } catch (error) {
      console.log('Error: Bad Signature List value serialization');
    }
  }, []);

  useEffect(() => {
    if (props.onChange)
      props.onChange(
        props.controlId,
        props.controlTypeId,
        value,
        valid(),
        null,
        null,
      );
  }, [value]);

  useEffect(() => {
    if (props.showError) valid();
  }, [props.showError]);

  function getNewItem(): SignatureListItem {
    let nextIndex = 0;
    if (items && items.length > 0)
      nextIndex = Math.max(...items.map(o => o.index)) + 1;

    let newItem: SignatureListItem = {
      index: nextIndex,
      person: '',
      personName: '',
    };

    return newItem;
  }

  async function getPersonName(itemValue: string): Promise<string> {
    let personName = '';

    let valObj:
      | {
          mongoId: string;
          SQLServerId: string;
        }
      | undefined = undefined;

    try {
      valObj = JSON.parse(itemValue) as {
        mongoId: string;
        SQLServerId: string;
      };

      if (valObj) {
        let person = people.filter(x => x._id == valObj!.mongoId)[0];
        if (person) personName = person.firstName + ' ' + person.lastName;
      }
    } catch (error) {
      console.log('Error: Bad Signature List value serialization');
    }

    return personName;
  }

  function showModal(item: SignatureListItem | null): void {
    if (props.disabled) return;

    setSelectedItem(item ? {...item} : getNewItem());
    setIsShowingModal(true);
    setSignatureShowErrors(false);
    setNameError('');
    setSignatureError('');
  }

  function hideModal(): void {
    setIsShowingModal(false);
  }

  async function changePerson(person: string, isValid: boolean): Promise<void> {
    setNameError('');

    let currentItem = {...selectedItem!};

    if (isValid) {
      currentItem.person = person;
      currentItem.personName = await getPersonName(person);
    } else {
      currentItem.person = '';
      currentItem.personName = '';
    }

    setSelectedItem(currentItem);
  }

  function clearSignature(): void {
    if (signatureRef.current) signatureRef.current.clear();
  }

  async function saveSignature(): Promise<void> {
    let signature = '';
    if (signatureRef.current)
      signature = signatureRef.current.getCanvas().toDataURL();

    // Validations
    if (selectedItem!.person === '') {
      setSignatureShowErrors(true);
      setNameError('Please fill out name to save.');
      return;
    }

    if (
      items.some(
        x =>
          x.person &&
          selectedItem!.person &&
          (JSON.parse(x.person) as MongoIdSqlId).mongoId ===
            (JSON.parse(selectedItem!.person) as MongoIdSqlId).mongoId,
      )
    ) {
      setSignatureShowErrors(true);
      setNameError('This person has already been added to the list.');
      return;
    }

    props.setIsLoading(true);
    setIsShowingModal(false);

    var file = attachmentService.base64toFile(
      signature,
      Math.floor(Date.now() / 1000).toString(),
    );

    let signatureImageUri = await attachmentService.pushAttachmentChunks(
      props.submissionId,
      props.attachmentsType,
      file.name,
      file,
    );

    let currentItem = {...selectedItem!};
    currentItem.attachment = {
      refId: props.submissionId,
      refType: props.attachmentsType,
      controlId: props.controlId,
      fileId: props.submissionId,
      uri: '',
      name: file.name,
      type: file.type,
      size: file.size,
      blobUri: signatureImageUri,
    } as DynamicAttachment;

    let newSasUris = [...sasUris];
    let sasUri = await attachmentService.getSasUri(signatureImageUri);
    newSasUris.push({
      uri: signatureImageUri,
      sasUri: sasUri,
    });

    let itemsArray = [...items];
    let itemIndex = itemsArray.findIndex(i => i.index === currentItem.index);
    if (itemIndex !== -1) {
      itemsArray[itemIndex].person = currentItem.person;
      itemsArray[itemIndex].attachment = currentItem.attachment;
    } else itemsArray.push(currentItem);

    setItems(itemsArray);
    setValue(JSON.stringify(itemsArray));
    setSasUris(newSasUris);

    props.setIsLoading(false);
  }

  function remove(item: SignatureListItem): void {
    setiIemToRemove(item);
    deleteModalCommand.current.openModal();
  }

  async function confirmRemove(): Promise<void> {
    let newItems = [...items];

    let arrayIndex = newItems.indexOf(itemToRemove!);
    if (arrayIndex !== -1) {
      newItems.splice(arrayIndex, 1);
      setItems(newItems);
      setValue(JSON.stringify(newItems));
      setiIemToRemove(null);
    }

    if (itemToRemove?.attachment?.blobUri) {
      await attachmentService.deleteBlobAttachment(
        itemToRemove?.attachment?.blobUri,
      );
    }
  }

  function expand(item: SignatureListItem): void {
    let newExpandedItems = [...expandedItems];

    newExpandedItems.push(item.index);

    setExpandedItems(newExpandedItems);
  }

  function collapse(item: SignatureListItem): void {
    let newExpandedItems = [...expandedItems];

    let arrayIndex = newExpandedItems.indexOf(item.index);
    if (arrayIndex !== -1) {
      newExpandedItems.splice(arrayIndex, 1);
      setExpandedItems(newExpandedItems);
    }
  }

  function valid(): boolean {
    let isValid = true;
    let error = '';

    if (props.config?.required && (value === '' || value === '[]')) {
      isValid = false;
      error =
        (props.label ? '"' + props.label + '"' : 'This field') + ' is required';
    }

    if (props.showError) setError(error);

    return isValid;
  }

  const renderSignatureList = (): React.ReactElement => {
    return (
      <View style={{marginVertical: 12}}>
        {items.map(item => {
          let sasUri = sasUris.find(x => x.uri == item.attachment?.blobUri);
          let uri = sasUri?.sasUri ? sasUri.sasUri : '';

          return (
            <View
              key={item.index}
              style={[
                ReportsStyleSheet.listItemContainer,
                {
                  flexWrap: 'wrap',
                  borderWidth: 1,
                  borderColor: Colors.darkGreen,
                  borderRadius: 12,
                  padding: 12,
                  marginBottom: 12,
                },
              ]}>
              <Text
                style={[
                  ReportsStyleSheet.listItemName,
                  {paddingHorizontal: 12, paddingTop: 4},
                ]}>
                {item.personName}
              </Text>
              {!props.disabled && (
                <Pressable
                  style={({pressed}) => [
                    {
                      width: 28,
                      height: 28,
                      borderRadius: 28,
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginHorizontal: 12,
                    },
                    pressed && {
                      backgroundColor: Colors.darkGreenTransparent,
                    },
                  ]}
                  disabled={props.disabled}
                  onPress={() => remove(item)}>
                  <Icon icon={'trash'} color={Colors.darkestGreen} size={20} />
                </Pressable>
              )}
              {!expandedItems.includes(item.index) && (
                <Pressable
                  style={({pressed}) => [
                    {
                      width: 28,
                      height: 28,
                      borderRadius: 28,
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginHorizontal: 6,
                    },
                    pressed && {
                      backgroundColor: Colors.darkGreenTransparent,
                    },
                  ]}
                  onPress={() => expand(item)}>
                  <Icon icon={'expand'} color={Colors.darkestGreen} size={20} />
                </Pressable>
              )}
              {expandedItems.includes(item.index) && (
                <React.Fragment>
                  <Pressable
                    style={({pressed}) => [
                      {
                        width: 28,
                        height: 28,
                        borderRadius: 28,
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginHorizontal: 6,
                      },
                      pressed && {
                        backgroundColor: Colors.darkGreenTransparent,
                      },
                    ]}
                    onPress={() => collapse(item)}>
                    <Icon
                      icon={'collapse'}
                      color={Colors.darkestGreen}
                      size={20}
                    />
                  </Pressable>
                  {uri ? (
                    <View style={{flexBasis: '100%'}}>
                      <View
                        style={{
                          width: 400,
                          backgroundColor: Colors.white,
                          borderColor: Colors.white,
                          borderWidth: 1,
                          borderRadius: 20,
                        }}>
                        <Image
                          resizeMode={'contain'}
                          style={{width: '100%', height: 240}}
                          source={{uri: uri}}
                        />
                      </View>
                    </View>
                  ) : null}
                </React.Fragment>
              )}
            </View>
          );
        })}
      </View>
    );
  };

  const renderModal = (): React.ReactElement => {
    return (
      <Modal
        visible={isShowingModal}
        transparent={true}
        statusBarTranslucent={true}
        animationType='fade'>
        <View style={ControlsStyleSheet.modalBackground}>
          <View style={[ControlsStyleSheet.groupSelectorModal, {width: 440, height: 'auto'}]}>
            <View style={ControlsStyleSheet.groupSelectorModalBar}>
              <Text style={ControlsStyleSheet.groupSelectorModalTitle}>
                {props.label}
              </Text>
              <Pressable
                style={({pressed}) => [
                  ControlsStyleSheet.groupSelectorModalClose,
                  pressed && {
                    backgroundColor: Colors.darkGreenTransparent,
                    borderRadius: 24,
                  },
                ]}
                onPress={hideModal}>
                <Icon icon={'close'} color={Colors.darkestGreen} size={24} />
              </Pressable>
            </View>
            <View>
              <View style={{marginTop: 12}}>
                <DynamicAutocomplete
                  label='Name'
                  config={{
                    optionSource: ListDataTypes.PEOPLE,
                  }}
                  value={selectedItem?.person ?? ''}
                  onChange={(controlId, controlTypeId, value, isValid) =>
                    changePerson(value, isValid)
                  }
                  showError={signatureShowErrors}
                />
                <Text style={[ControlsStyleSheet.error, {marginTop: -12}]}>
                  {nameError}
                </Text>
              </View>
              <View style={{marginTop: 12}}>
                <Text style={ControlsStyleSheet.label}>Signature</Text>
                <View style={{width: 400}}>
                  <View
                    style={{
                      backgroundColor: Colors.white,
                      borderColor: Colors.darkGreen,
                      borderWidth: 1,
                      borderRadius: 20,
                    }}>
                    <SignatureCanvas
                      ref={signatureRef}
                      canvasProps={{height: 240}}
                    />
                  </View>
                  <View
                    style={{
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      backgroundColor: Colors.white,
                      borderRadius: 6,
                    }}>
                    <Pressable
                      style={({pressed}) => [
                        ControlsStyleSheet.autocompleteButton,
                        pressed && {
                          backgroundColor: Colors.darkGreenTransparent,
                        },
                      ]}
                      onPress={clearSignature}>
                      <Text style={ControlsStyleSheet.autocompleteButtonText}>
                        Clear
                      </Text>
                    </Pressable>
                  </View>
                </View>
                <Text style={ControlsStyleSheet.error}>{signatureError}</Text>
              </View>
            </View>
            <Pressable
              style={({pressed}) => [
                CommonStyleSheet.greenButton,
                {alignSelf: 'center', marginTop: 'auto', marginBottom: 24},
                pressed && {
                  backgroundColor: Colors.darkGreenTransparent,
                },
              ]}
              onPress={saveSignature}>
              <Text style={CommonStyleSheet.greenButtonText}>Save</Text>
            </Pressable>
          </View>
        </View>
      </Modal>
    );
  };

  return (
    <View style={{display: props.visible === false ? 'none' : 'flex'}}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 24,
        }}>
        <Text style={ControlsStyleSheet.labelLarge}>
          {props.label}
          <Text
            style={[
              ControlsStyleSheet.required,
              {fontSize: 16, verticalAlign: 'middle'},
            ]}>
            {props.config?.required ? '*' : ''}
          </Text>
        </Text>
        <Pressable
          style={({pressed}) => [
            ControlsStyleSheet.linkRecordAddButton,
            pressed && {
              backgroundColor: Colors.darkGreenTransparent,
            },
          ]}
          disabled={props.disabled}
          onPress={() => showModal(null)}>
          <Icon icon={'plus'} color={Colors.darkestGreen} size={24} />
        </Pressable>
      </View>
      {renderSignatureList()}
      {renderModal()}
      <Text style={ControlsStyleSheet.error}>{error}</Text>
      <ConfirmModal
        title='Remove this signature'
        message='Are you sure you want to remove this signature?'
        cancelClick={() => {}}
        cancelText='No'
        okClick={() => {
          confirmRemove();
        }}
        okText='Remove'
        setOpenModal={(func: () => void) => {
          deleteModalCommand.current.setOpenModal(func);
        }}
      />
    </View>
  );
};

export default DynamicSignatureList;
